import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FaComment } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import useLanguage from '../../../../../hooks/account/useLanguage';
import Language, { normalizeLanguage } from '../../../../../types/language';
import { setLanguage } from '../../../../../redux/slices/applicationSlice';
import useAuth from '../../../../../hooks/account/useAuth';
import useProfilePicture from '../../../../../hooks/account/useProfilePicture';
import ProfilePicture from '../../../misc/ProfilePicture';
import i18n from '../../../../../locales/i18n';

/** Top-bar utilities: language, feedback, and account avatar. */
export default function TopBarActions(): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lang = normalizeLanguage(useLanguage());
  const oppositeLang = lang === Language.NL ? Language.EN : Language.NL;
  const auth = useAuth();
  const profilePicture = useProfilePicture();

  const displayName = auth ? `${auth.firstName} ${auth.lastName}`.trim() : '';

  const handleLanguageToggle = () => {
    dispatch(setLanguage(oppositeLang));
    i18n.changeLanguage(oppositeLang).catch(() => undefined);
  };

  return (
    <div className="flex items-center gap-3 sm:gap-4">
      <button
        type="button"
        onClick={handleLanguageToggle}
        className="flex items-center gap-2 rounded-[8px] px-2 py-1.5 text-sm font-medium text-primary-900 transition-colors hover:bg-secondary-200"
        aria-label={t('general.switchLanguage')}>
        <img
          src={`${process.env.PUBLIC_URL}/assets/flags/${lang}.svg`}
          alt={lang}
          className="h-4 w-4"
        />
        <span className="hidden uppercase sm:inline">{lang}</span>
      </button>

      <button
        type="button"
        onClick={() => navigate('/feedback')}
        className="flex items-center gap-2 rounded-[8px] px-2 py-1.5 text-sm font-medium text-primary-900 transition-colors hover:bg-secondary-200">
        <FaComment className="h-4 w-4 text-primary-300" />
        <span className="hidden sm:inline">{t('feedback.title')}</span>
      </button>

      {auth && (
        <Link
          to="/profile"
          className="flex items-center gap-2 rounded-[8px] px-1 py-1 transition-colors hover:bg-secondary-200">
          <ProfilePicture
            picture={profilePicture}
            alias={`${auth.firstName}+${auth.lastName}`}
            className="h-9 w-9 rounded-full"
          />
          {displayName && (
            <span className="hidden max-w-[140px] truncate text-sm font-medium text-primary-900 lg:inline">
              {displayName}
            </span>
          )}
        </Link>
      )}
    </div>
  );
}
